import axios, { AxiosError } from 'axios';

import { Team, TeamGroup, TeamMember } from '@tassomutuo-backoffice/types';
import { axiosApiInstancePrivate } from '..';

export const promoteUserToTeamManager = async (userId: string) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/promote-to-team-manager/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const removeUserFromTeamManager = async (userId: string) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/remove-from-team-manager/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getAllTeams = async (): Promise<Team[]> => {
  try {
    const response = await axiosApiInstancePrivate.get<Team[]>(`/v1/teams`);

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const addMemberToTeam = async (teamId: string, userId: string) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/${teamId}/add-member/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const removeMemberFromTeam = async (teamId: string, userId: string) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/${teamId}/remove-member/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

interface UsersResponse {
  results: TeamMember[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export const getTeamMembers = async (
  teamId: string,
  query: {
    page: number;
    pageSize: number;
    searchText?: string;
  }
) => {
  const params: { page: number; limit: number; email?: string } = {
    page: query.page,
    limit: query.pageSize,
  };
  if (query.searchText) {
    params['email'] = query.searchText;
  }

  try {
    const response = await axiosApiInstancePrivate.get<UsersResponse>(
      `/v1/teams/${teamId}/members`,
      {
        params,
      }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const createTeamGroup = async (
  teamId: string,
  teamGroupData: Omit<TeamGroup, 'id' | 'team'>
) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/${teamId}/groups`,
      teamGroupData
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

interface TeamGroupsResponse {
  results: TeamGroup[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export const getTeamGroups = async (
  teamId: string,
  query: {
    page: number;
    pageSize: number;
  }
) => {
  try {
    const params: { page: number; limit: number } = {
      page: query.page,
      limit: query.pageSize,
    };
    const response = await axiosApiInstancePrivate.get<TeamGroupsResponse>(
      `/v1/teams/${teamId}/groups`,
      {
        params,
      }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getAllTeamGroup = async (teamId: string) => {
  try {
    const response = await axiosApiInstancePrivate.get<TeamGroup[]>(
      `/v1/teams/${teamId}/groups/all`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const addMemberToGroup = async (groupId: string, userId: string) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/groups/${groupId}/add-member/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const removeMemberFromGroup = async (
  groupId: string,
  userId: string
) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/groups/${groupId}/remove-member/${userId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const removeTeamGroup = async (groupId: string) => {
  try {
    const response = await axiosApiInstancePrivate.delete<void>(
      `/v1/teams/groups/${groupId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const changeTeamGroupLeaders = async (
  groupId: string,
  leaders: string[]
) => {
  try {
    const response = await axiosApiInstancePrivate.put<void>(
      `/v1/teams/groups/${groupId}/leaders`,
      { leaders }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const getTeamGroupMember = async (
  groupId: string,
  query: {
    page: number;
    pageSize: number;
    searchText?: string;
  }
) => {
  try {
    const params: { page: number; limit: number; email?: string } = {
      page: query.page,
      limit: query.pageSize,
    };
    if (query.searchText) {
      params['email'] = query.searchText;
    }
    const response = await axiosApiInstancePrivate.get<UsersResponse>(
      `/v1/teams/groups/${groupId}/members`,
      {
        params,
      }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const sendNotificationAlertTeamManager = async (
  teamId: string,
  includeChief: boolean = false
) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/${teamId}/send-alert-team-manager`,
      {
        includeChief,
      }
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const sendNotificationAlertTeamMember = async (
  teamId: string,
  teamMemberId: string
) => {
  try {
    const response = await axiosApiInstancePrivate.post<void>(
      `/v1/teams/${teamId}/send-alert-team-member/${teamMemberId}`
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

export const updateSpecificCapUserCollaboratori = async (
  userId: string,
  data: {
    capType: 'Ufficio' | '2' | '3' | '4' | '5';
    cap: string | null;
    comune: string | null;
    provincia: string | null;
    regione: string | null;
  }
) => {
  try {
    const response = await axiosApiInstancePrivate.patch<void>(
      `/v1/users/${userId}/user-collaboratori/update-specific-cap`,
      data
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};


export const updateUserTelephone = async (
  userId: string,
  telephoneNumber: string
): Promise<void> => {
  try {
    const response = await axiosApiInstancePrivate.patch<void>(
      `/v1/users/${userId}/user-collaboratori/update-specific-phone-number`,
      { telephoneNumber }
    );

    return response?.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};
